import React, { useMemo } from 'react'

import Localization from '~/client/src/shared/localization/LocalizationManager'
import type Company from '~/client/src/shared/models/Company'
import type LocationAttributeBase from '~/client/src/shared/models/LocationObjects/LocationAttributeBase'
import type User from '~/client/src/shared/models/User'
import { cn } from '~/client/src/shared/utils/cn'

import {
  FilterValueCategory,
  FilterValueCompany,
  FilterValueFileExtension,
  FilterValueFileType,
  FilterValueLocation,
  FilterValueProject,
  FilterValueStatus,
  FilterValueUser,
  FilterValueWorkflowType,
} from './SavedViewFilterRenderers'
import {
  BUILT_IN_FILTER_TYPES,
  FilterTypeDescriptor,
  FilterValue,
} from './savedViewsTypes'

function formatRemovedFilterLabel(filterKey: string): string {
  const name = filterKey.startsWith('data.') ? filterKey.slice(5) : filterKey
  return `${name} (${Localization.translator.removed})`
}

interface SavedViewFiltersProps {
  appliedFilters: { [facetKey: string]: Set<string> }
  groupBy?: string
  className?: string
  facets?: FilterTypeDescriptor[]
  groupingEnabled?: boolean
}

const SavedViewFilters: React.FC<SavedViewFiltersProps> = ({
  appliedFilters,
  groupBy,
  className,
  facets = [],
  groupingEnabled = true,
}) => {
  const filtersCount = useMemo(() => {
    return Object.values(appliedFilters).reduce((sum, set) => sum + set.size, 0)
  }, [appliedFilters])

  const filtersMap = useMemo(() => {
    const map = new Map<string, FilterValue[]>()
    Object.entries(appliedFilters).forEach(([facetKey, valueSet]) => {
      if (valueSet.size > 0) {
        map.set(facetKey, Array.from(valueSet))
      }
    })
    return map
  }, [appliedFilters])
  const getFacet = (filterKey: string): FilterTypeDescriptor | undefined => {
    return facets.find(f => f.key === filterKey)
  }

  const renderBuiltInFilterValue = (
    filterType: string | undefined,
    value: FilterValue,
    key: string,
  ): JSX.Element | null => {
    if (!filterType) return null

    switch (filterType) {
      case BUILT_IN_FILTER_TYPES.COMPANY:
        return <FilterValueCompany key={key} value={value as Company} />

      case BUILT_IN_FILTER_TYPES.USER:
        return <FilterValueUser key={key} value={value as User} />

      case BUILT_IN_FILTER_TYPES.LOCATION:
        return (
          <FilterValueLocation
            key={key}
            value={value as LocationAttributeBase}
          />
        )

      case BUILT_IN_FILTER_TYPES.EQUIPMENT:
        return <FilterValueLocation key={key} value={value} />

      case BUILT_IN_FILTER_TYPES.WORKFLOW_CATEGORY:
        return <FilterValueCategory key={key} value={value} />

      case BUILT_IN_FILTER_TYPES.WORKFLOW_TYPE:
        return <FilterValueWorkflowType key={key} value={value} />

      case BUILT_IN_FILTER_TYPES.WORKFLOW_STATUS:
        return <FilterValueStatus key={key} value={value} />

      case BUILT_IN_FILTER_TYPES.PROJECT:
        return <FilterValueProject key={key} value={value} />

      case BUILT_IN_FILTER_TYPES.FILE_TYPE:
        return <FilterValueFileType key={key} value={value} />

      case BUILT_IN_FILTER_TYPES.FILE_EXTENSION:
        return <FilterValueFileExtension key={key} value={value} />

      default:
        return null
    }
  }

  const renderValue = (
    filterKey: string,
    value: FilterValue,
    index: number,
  ): JSX.Element => {
    const facet = getFacet(filterKey)
    const key = `${filterKey}-${index}`

    if (facet?.renderFilterValue) {
      const customRender = facet.renderFilterValue(value)
      if (customRender !== null) {
        return customRender
      }
    }

    const builtInRender = renderBuiltInFilterValue(facet?.type, value, key)
    if (builtInRender !== null) {
      return builtInRender
    }

    const displayValue =
      typeof value === 'string' ? value : value?.name || String(value)
    return (
      <div
        key={key}
        className="tw:inline-flex tw:text-body-sm tw:text-foreground tw:overflow-hidden tw:whitespace-nowrap tw:text-ellipsis tw:max-w-[270px]"
      >
        {displayValue}
      </div>
    )
  }

  return (
    <div className={className}>
      {groupingEnabled && (
        <div className="tw:flex tw:items-start tw:h-10 tw:self-stretch tw:px-4 tw:py-2">
          <div className="tw:text-title-sm tw:text-foreground tw:grow-0 tw:inline-block tw:align-middle tw:min-w-[200px] tw:max-w-[200px] tw:py-[10px]">
            {Localization.translator.groupBy}
          </div>
          <div className="tw:text-title-sm tw:text-foreground tw:grow-0 tw:inline-block tw:align-middle tw:min-w-[200px] tw:max-w-[200px] tw:py-[10px]">
            {groupBy || Localization.translator.none}
          </div>
        </div>
      )}
      <div className="tw:flex tw:items-start tw:gap-2 tw:px-4 tw:h-10 tw:self-stretch">
        <span className="tw:text-title-sm tw:text-foreground tw:grow-0 tw:inline-block tw:align-middle">
          {Localization.translator.filters}
        </span>
        <span className="tw:text-body-sm tw:text-foreground tw:inline-block tw:align-middle">
          ({filtersCount || 0})
        </span>
      </div>
      {filtersMap &&
        Array.from(filtersMap.entries()).map(([filterKey, values]) => {
          const facet = getFacet(filterKey)
          const isRemoved = !facet
          const label = facet?.label || formatRemovedFilterLabel(filterKey)
          return (
            <div
              key={filterKey}
              className="tw:flex tw:items-baseline tw:self-stretch tw:px-4 tw:py-2"
            >
              <div className="tw:min-w-[200px] tw:max-w-[200px] tw:px-2 tw:py-[10px]">
                <span
                  className={cn({
                    'tw:text-body-md tw:text-foreground tw:inline-block tw:align-middle': true,
                    'tw:text-md3-on-surface-variant tw:italic': isRemoved,
                    'tw:text-md3-on-surface': !isRemoved,
                  })}
                >
                  {label}
                </span>
                <span className="tw:text-body-sm tw:text-foreground tw:inline-block tw:align-middle tw:ml-2">
                  ({values.length})
                </span>
              </div>
              <div className="tw:flex tw:flex-wrap tw:items-start tw:gap-2">
                {values.map((value, index) => (
                  <div
                    key={`${filterKey}-${index}`}
                    className="tw:flex tw:items-center tw:grow-0 tw:[&>*]:flex-none"
                  >
                    {renderValue(filterKey, value, index)}
                    {index < values.length - 1 && (
                      <span className="tw:text-body-sm tw:text-foreground tw:inline-block tw:align-middle tw:ml-1">
                        ,
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )
        })}
    </div>
  )
}

export default SavedViewFilters
